import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';

interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  church_id: number;
  role_id: number;
  role_name?: string;
  church_name?: string;
}

interface AuthContextType {
  user: User | null;
  login: (emailOrUsername: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token and get user info
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await apiClient.get('/users/me');
      // The server hands back a fresh 400-day token each time, so a device in
      // use is never signed out (2026-10-20).
      const { token: fresh, ...me } = response.data || {};
      if (fresh) localStorage.setItem('token', fresh);
      setUser(me);
    } catch (error: any) {
      // Forget the token only when the server says it is no good. A dropped
      // connection, a server restart or a phone waking up offline used to land
      // here too and sign the browser out, which is not what failed.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
      }
      console.error('Token verification failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (emailOrUsername: string, password: string) => {
    try {
      const response = await apiClient.post('/users/login', {
        emailOrUsername,
        password,
      });

      const { token, user: userData } = response.data;
      
      // Store token
      localStorage.setItem('token', token);
      
      // Set user data
      setUser(userData);
      
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};